import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { ArrowLeft, ChevronRight, ChevronDown, Plus, X, Loader2, Filter, Lock } from 'lucide-react';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useUser } from '../contexts/UserContext';
import { useOptionalAuth } from '../contexts/AuthContext';
import {
  isSentImmutableQuote,
  isAdminViewerRole,
  quoteReadOnlyMarker,
  type QuoteImmutabilitySignals,
} from '../utils/quoteImmutability';
import { isDemoMode } from '../utils/demoMode';
import { toTitleCase, formatProductName } from '../utils/format';
import { categoryLabel } from '../utils/categoryLabel';
import { MatchDrawer } from '../components/MatchDrawer';
import { ChainToggle } from '../components/ChainToggle';
import { DistributorMemoryBadge } from '../components/DistributorMemoryBadge';
import { QuoteReviewBar } from '../components/QuoteReviewBar';
import { Drawer, DrawerContent } from '../components/ui/drawer';
import {
  createMenu,
  getMenuStatus,
  createQuote,
  getQuote,
  getGuestQuote,
  getMoreMatches,
  updateQuote,
  updateGuestQuote,
  toggleRepMemoryLock,
} from '../services/api';

// ─── Types (matching backend API responses) ──────────────────────────────────

interface AlignmentCandidate {
  id: string;
  position: number;
  tier: string;
  score: number | null;
  /** True when this candidate is a rep-scoped memory lock (2-pick auto-lock
   * or a manual ChainToggle lock) -- drives the connected/broken chain icon. */
  rep_memory?: boolean;
  /** Operational Memory Epic, Lane 2: true when this candidate is a
   * distributor-scoped ("house pick") memory match forced to position 1.
   * Only set when rep_memory is falsy for the same candidate. */
  distributor_memory?: boolean;
  distributor_name?: string | null;
  /** Operational Memory Epic, Lane 2 revision (Ruling 2): disambiguates a
   * distributor_memory candidate into "preference" (presentation only) or
   * "mandate" (attributable, must show who/why). null/legacy => preference. */
  distributor_signal_type?: 'preference' | 'mandate' | null;
  distributor_mandate_reason?: string | null;
  distributor_mandate_set_by?: string | null;
  product: {
    id: string;
    item_number: string;
    brand: string;
    product: string;
    pack_size: string;
    category: string;
  };
}

interface QuoteLine {
  id: string;
  position: number;
  component: {
    id: string;
    name: string;
    source_dish: string;
    canonical_key?: string | null;
  } | null;
  product: {
    id: string;
    item_number: string;
    brand: string;
    product: string;
    pack_size: string;
    category: string;
  };
  quantity: number;
  unit_price: string | null;
  unit_price_cents: number | null;
  category: string;
  alignment_selected: number;
  chef_note: string | null;
  alignment_candidates: AlignmentCandidate[];
}

interface QuoteData {
  id: string;
  status: string;
  working_label: string;
  lines: QuoteLine[];
}

interface Dish {
  id: string;
  name: string;
  components: string[];
  componentLines: Record<string, QuoteLine>;
}

// ─── Match status helper ──────────────────────────────────────────────────────

function getMatchStatus(line: QuoteLine | undefined, isMapped: boolean): string {
  if (isMapped) return 'Your Pick';
  if (!line?.product) return 'No Match';
  const bestCandidate = line.alignment_candidates?.find(c => c.position === 1);
  const score = bestCandidate?.score != null ? Math.round(bestCandidate.score * 100) : null;
  if (score === null || score < 50) return 'Needs Your Call';
  if (score < 70) return 'Review Suggested';
  if (score < 90) return 'Good Match';
  return 'Strong Match';
}

const MATCH_STATUS_OPTIONS = ['Strong Match', 'Good Match', 'Review Suggested', 'Needs Your Call', 'Your Pick', 'No Match'] as const;

// ─── Filter Dropdown ─────────────────────────────────────────────────────────

function FilterDropdown({ label, options, selected, onToggle, onClear, formatOption }: {
  label: string;
  options: string[];
  selected: Set<string>;
  onToggle: (value: string) => void;
  onClear: () => void;
  formatOption?: (v: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const activeCount = selected.size;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
          activeCount > 0
            ? 'bg-[#A5CFDD]/15 text-[#2A5F6F] border-[#A5CFDD]'
            : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
        }`}
      >
        <Filter className="w-3 h-3" />
        {label}
        {activeCount > 0 && (
          <span className="bg-[#A5CFDD] text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
            {activeCount}
          </span>
        )}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {activeCount > 0 && (
            <button
              onClick={() => { onClear(); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-xs text-[#A5CFDD] hover:bg-gray-50 border-b border-gray-100"
            >
              Clear {label}
            </button>
          )}
          {options.map(option => (
            <label
              key={option}
              className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={selected.has(option)}
                onChange={() => onToggle(option)}
                className="rounded border-gray-300 text-[#A5CFDD] focus:ring-[#A5CFDD]"
              />
              <span className="text-gray-700 truncate">{formatOption ? formatOption(option) : option}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MapIngredientsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { quotesRemaining } = useUser();
  const demo = isDemoMode();
  // P0 route/shell guard item 1: a QM admin deep-linking here (e.g.
  // /map-ingredients?quoteId=...) is a viewer whose writes the server intends
  // to refuse, so the page renders read-only with an explicit marker instead
  // of offering write controls. useOptionalAuth (not useAuth) so unit tests
  // can mount this page without the provider stack; during impersonation the
  // /me role is the impersonated user's, so impersonation is unaffected.
  const auth = useOptionalAuth();
  const adminViewer = isAdminViewerRole(auth?.user?.role);

  // Router state passed from StartNewQuotePage
  const routerMenuId: string | undefined = (location.state as any)?.menuId;
  const routerQuoteId: string | undefined = (location.state as any)?.quoteId;
  const isOpenQuote: boolean = (location.state as any)?.isOpenQuote || false;
  const locationId: string | undefined = (location.state as any)?.locationId;

  // Also read quoteId from URL query params (for email links)
  const urlQuoteId = new URLSearchParams(location.search).get('quoteId');

  // ── Core state ──
  const [quoteId, setQuoteId] = useState<string | null>(routerQuoteId || urlQuoteId || null);
  const [menuId, setMenuId] = useState<string | null>(routerMenuId || null);
  // P0 route/shell guard: status/state/sent_at captured from every quote
  // fetch so sent/accepted quotes render read-only (see readOnly below).
  const [quoteMeta, setQuoteMeta] = useState<QuoteImmutabilitySignals | null>(null);
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [selectedDish, setSelectedDish] = useState<Dish | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState('Processing menu…');
  // Justin's third criterion: no feedback surface before there is an actual
  // match state to review. `loading` cannot answer that -- it is set false on
  // the failure and 30-attempt-timeout paths too, so gating on !loading would
  // ask "how do these matches look?" over a menu that never processed. Set
  // true ONLY where matching genuinely finished.
  const [matchingComplete, setMatchingComplete] = useState(false);

  // Operational Memory Epic, Lane 1 revision (Ruling 3): ChainToggle is a
  // real bidirectional lock, not a read-only bookmark, so its state can
  // change without a full quote refetch. lockOverrides tracks any product
  // whose lock has been flipped in this session; falls back to the
  // server-provided candidate.rep_memory when no override exists yet.
  const [lockOverrides, setLockOverrides] = useState<Record<string, boolean>>({});
  const [lockPending, setLockPending] = useState<string | null>(null);
  // W2 — same silent-failure defect as MatchDrawer, second site. Keyed by
  // product so the message appears on the row the rep actually clicked rather
  // than as a page-level banner detached from the control.
  const [lockError, setLockError] = useState<{ productId: string; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ── Tab / drawer state ──
  const [selectedTab, setSelectedTab] = useState<'dishes' | 'categories' | 'match-strength'>('categories');
  const [strengthFilters, setStrengthFilters] = useState<Set<string>>(new Set(['no-match', 'needs-review']));
  const [mapDrawerOpen, setMapDrawerOpen] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState('');
  const [mappedComponents, setMappedComponents] = useState<Record<string, string[]>>({});
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [isDishListDrawerOpen, setIsDishListDrawerOpen] = useState(false);

  const [isAddDishDrawerOpen, setIsAddDishDrawerOpen] = useState(false);
  const [newDishName, setNewDishName] = useState('');
  const [newDishComponents, setNewDishComponents] = useState('');
  const [addDishLoading, setAddDishLoading] = useState(false);

  // ── Column filter state ──
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());
  const [categoryFilter, setCategoryFilter] = useState<Set<string>>(new Set());
  const [brandFilter, setBrandFilter] = useState<Set<string>>(new Set());

  // ── Additions tracking (manually added dishes + "Add to Quote" products) ──
  const [additions, setAdditions] = useState<Array<{
    id: string;
    componentName: string;
    sourceDish: string;
    product: { id: string; brand: string; product: string; item_number: string; pack_size: string; category: string };
    type: 'added_dish' | 'add_to_quote';
  }>>([]);


  // ─── Filter helpers ──────────────────────────────────────────────────────

  const allLines = useMemo(() => {
    const seen = new Set<string>();
    const result: { component: string; line: QuoteLine; dish: Dish }[] = [];
    for (const dish of dishes) {
      for (const comp of dish.components) {
        const line = dish.componentLines[comp];
        const key = line?.product?.id || comp;
        if (seen.has(key)) continue;
        seen.add(key);
        result.push({ component: comp, line, dish });
      }
    }
    return result;
  }, [dishes]);

  const availableCategories = useMemo(() => {
    const cats = new Set<string>();
    for (const { line } of allLines) {
      const cat = line?.category || line?.product?.category;
      if (cat) cats.add(cat);
    }
    return Array.from(cats).sort();
  }, [allLines]);

  const availableBrands = useMemo(() => {
    const brands = new Set<string>();
    for (const { line } of allLines) {
      if (line?.product?.brand) brands.add(line.product.brand);
    }
    return Array.from(brands).sort();
  }, [allLines]);

  const hasActiveFilters = statusFilter.size > 0 || categoryFilter.size > 0 || brandFilter.size > 0;

  const passesFilters = useCallback((component: string, line: QuoteLine | undefined): boolean => {
    if (!hasActiveFilters) return true;
    if (statusFilter.size > 0) {
      const status = getMatchStatus(line, (mappedComponents[component]?.length || 0) > 0);
      if (!statusFilter.has(status)) return false;
    }
    if (categoryFilter.size > 0) {
      const cat = line?.category || line?.product?.category || '';
      if (!categoryFilter.has(cat)) return false;
    }
    if (brandFilter.size > 0) {
      const brand = line?.product?.brand || '';
      if (!brandFilter.has(brand)) return false;
    }
    return true;
  }, [hasActiveFilters, statusFilter, categoryFilter, brandFilter, mappedComponents]);

  const filteredCount = useMemo(() => {
    if (!hasActiveFilters) return allLines.length;
    return allLines.filter(({ component, line }) => passesFilters(component, line)).length;
  }, [allLines, hasActiveFilters, passesFilters]);

  const toggleFilter = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, value: string) => {
    setter(prev => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  };

  const clearAllFilters = () => {
    setStatusFilter(new Set());
    setCategoryFilter(new Set());
    setBrandFilter(new Set());
  };

  // ─── Build dish list from quote lines ─────────────────────────────────────

  // A "match" here is exactly what getMatchStatus calls one: a line carrying a
  // product. A line with no product renders as "No Match", so counting lines
  // would let the card appear over a list where nothing matched at all.
  const matchCount = dishes.reduce(
    (total, dish) =>
      total + dish.components.filter((c) => dish.componentLines[c]?.product).length,
    0,
  );

  function buildDishesFromLines(lines: QuoteLine[]): Dish[] {
    const dishMap: Record<string, Dish> = {};
    for (const line of lines) {
      const comp = line.component;
      if (!comp) continue;
      const dishKey = comp.source_dish || 'Unknown Dish';
      if (!dishMap[dishKey]) {
        dishMap[dishKey] = { id: dishKey, name: dishKey, components: [], componentLines: {} };
      }
      dishMap[dishKey].components.push(comp.name);
      dishMap[dishKey].componentLines[comp.name] = line;
    }
    return Object.values(dishMap);
  }

  const isGuest = !localStorage.getItem('quoteme_token');
  const fetchQuote = (id: string) => isGuest ? getGuestQuote(id) : getQuote(id);
  const persistQuote = (id: string, updates: any) => isGuest ? updateGuestQuote(id, updates) : updateQuote(id, updates);

  // P0 route/shell guard: capture the immutability signals from any fetched
  // quote payload (the local QuoteData interface only types the fields this
  // page renders; status/state/sent_at ride along on the same response).
  const captureQuoteMeta = (data: any) => {
    setQuoteMeta({ status: data?.status, state: data?.state, sent_at: data?.sent_at });
  };

  // Sent immutability (item 3) + admin viewer (item 1): every write control
  // on this page (dish checkbox, Manually Add, Add Match / Change Match,
  // memory-lock chain, Adjust Pricing, Looks good / Needs fixes feedback) is
  // hidden when readOnly, and a marker names why.
  const sentLocked = !!quoteMeta && isSentImmutableQuote(quoteMeta);
  const readOnly = adminViewer || sentLocked;
  const readOnlyMarker = quoteReadOnlyMarker(adminViewer, sentLocked);

  // Sent immutability, dismiss-on-flip. The write drawers here are rendered
  // unconditionally, so hiding their triggers does not dismiss one that is
  // ALREADY open when the quote goes out mid-session.
  //
  // The rule for choosing a render gate here is written out in full on
  // QuoteBuilderPage, above its equivalent effect. In short: prefer the gate
  // that still explains itself, and dismiss only as a last resort.
  //
  // MatchDrawer can render itself read-only (it takes readOnly and swaps its
  // write actions for a "matches are locked" marker), so it is left mounted.
  // Dismissing it as well, as an earlier round did, made that marker
  // unreachable dead UI, because handleMapComponent already refuses to OPEN the
  // drawer read-only, so nothing could ever reach a read-only MatchDrawer.
  //
  // Add Dish has neither a read-only mode nor a single disableable control, so
  // dismissal IS its render gate, in front of handleAlignWithCatalog's belt.
  useEffect(() => {
    if (!readOnly) return;
    setIsAddDishDrawerOpen(false);
  }, [readOnly]);

  // ─── Poll menu status then load quote ─────────────────────────────────────

  useEffect(() => {
    if (!menuId && !quoteId) {
      setLoading(false);
      setError('No menu provided. Please start a new quote.');
      return;
    }

    let cancelled = false;

    async function pollAndLoad() {
      try {
        // If we already have a quoteId, just load it
        if (quoteId) {
          setLoadingStatus('Loading matches…');
          const res = await fetchQuote(quoteId);
          if (cancelled) return;
          if (res.error || !res.data) throw new Error(res.error || 'Failed to load quote');
          captureQuoteMeta(res.data);
          const built = buildDishesFromLines((res.data as QuoteData).lines || []);
          setDishes(built);
          setSelectedDish(built[0] || null);
          // Entering with an existing quoteId skips the poll entirely, because
          // matching finished on a previous visit. Without this the card would
          // never appear on a revisit.
          setMatchingComplete(true);
          setLoading(false);
          return;
        }

        // Poll menu status until quote is ready
        setLoadingStatus('Processing menu…');
        let attempts = 0;
        let resolvedQuoteId: string | null = null;
        while (attempts < 30) {
          if (cancelled) return;
          const statusRes = await getMenuStatus(menuId!);
          if (statusRes.error) throw new Error(statusRes.error);
          const statusData = statusRes.data;
          if (statusData?.status === 'processed' || statusData?.status === 'completed') {
            resolvedQuoteId = statusData.quote_id || null;
            break;
          }
          if (statusData?.status === 'failed') throw new Error('Menu processing failed. Please try again.');
          await new Promise(r => setTimeout(r, 2000));
          attempts++;
        }
        if (attempts >= 30) throw new Error('Menu processing timed out. Please try again.');

        if (!resolvedQuoteId) {
          // Fallback: create quote from menu
          setLoadingStatus('Building quote…');
          const quoteRes = await createQuote(menuId!);
          if (cancelled) return;
          if (quoteRes.error || !quoteRes.data) throw new Error(quoteRes.error || 'Failed to create quote');
          resolvedQuoteId = quoteRes.data.id;
        }
        setQuoteId(resolvedQuoteId);

        // Load full quote with lines
        setLoadingStatus('Loading matches…');
        const fullQuote = await fetchQuote(resolvedQuoteId);
        if (cancelled) return;
        if (fullQuote.error || !fullQuote.data) throw new Error(fullQuote.error || 'Failed to load quote');
        captureQuoteMeta(fullQuote.data);
        const built = buildDishesFromLines((fullQuote.data as QuoteData).lines || []);
        setDishes(built);
        setSelectedDish(built[0] || null);
        // Only reachable once the poll broke on 'processed'/'completed'; the
        // 'failed' branch and the 30-attempt timeout both throw above.
        setMatchingComplete(true);
        setLoading(false);
      } catch (e: any) {
        if (!cancelled) {
          setError(e.message || 'Something went wrong');
          setLoading(false);
        }
      }
    }

    pollAndLoad();
    return () => { cancelled = true; };
  }, [menuId, quoteId]);

  // ─── Add dish manually ────────────────────────────────────────────────────

  async function handleAlignWithCatalog() {
    // Sent immutability: Manually Add is a write path (createMenu, which
    // provisions a menu plus quote server-side and folds its lines into this
    // surface). The trigger is hidden on a read-only render; guard the call
    // site too, matching handleToggleLock.
    if (readOnly) return;
    if (!newDishComponents.trim()) return;
    setAddDishLoading(true);
    try {
      const components = newDishComponents.split('\n').map(c => c.trim()).filter(Boolean);
      const text = newDishName
        ? `${newDishName}\n${components.join('\n')}`
        : components.join('\n');

      const menuRes = await createMenu({ raw_text: text, name: newDishName || 'Manual Dish' });
      if (menuRes.error || !menuRes.data) throw new Error(menuRes.error || 'Failed to create menu');
      const newQId = menuRes.data.quote_id;

      const fullQuote = await fetchQuote(newQId);
      if (fullQuote.error || !fullQuote.data) throw new Error(fullQuote.error);
      const newLines = (fullQuote.data as QuoteData).lines || [];
      const newDishes = buildDishesFromLines(newLines);

      // Merge new dishes into existing dish list
      setDishes(prev => [...prev, ...newDishes]);
      setSelectedDish(newDishes[0] || selectedDish);
      setSelectedTab('dishes');

      // Track as additions for sidebar
      for (const line of newLines) {
        if (line.product) {
          setAdditions(prev => [...prev, {
            id: `add-dish-${line.id}`,
            componentName: line.component?.name || 'Unknown',
            sourceDish: newDishName || 'Manual Dish',
            product: line.product,
            type: 'added_dish' as const,
          }]);
        }
      }

      if (!quoteId) setQuoteId(newQId);
    } catch (e: any) {
      console.error('Add dish error:', e.message);
    } finally {
      setAddDishLoading(false);
      setIsAddDishDrawerOpen(false);
      setIsDishListDrawerOpen(false);
      setNewDishName('');
      setNewDishComponents('');
    }
  }


  // ─── Review bar callback ──────────────────────────────────────────────────

  const handleMatchesUpdated = useCallback(async () => {
    if (!quoteId) return;
    try {
      const res = await fetchQuote(quoteId);
      if (res.error || !res.data) return;
      captureQuoteMeta(res.data);
      const built = buildDishesFromLines((res.data as QuoteData).lines || []);
      setDishes(built);
      setSelectedDish(built[0] || null);
    } catch (e) {
      console.error('Failed to refresh after review:', e);
    }
  }, [quoteId]);

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const [selectedLine, setSelectedLine] = useState<QuoteLine | null>(null);

  const handleMapComponent = (componentName: string) => {
    // Sent immutability: the match drawer is a write path
    // (submitYourCallSelection / toggleRepMemoryLock). Never open it on a
    // read-only render, mirroring QuoteBuilderPage's openMatchDrawer.
    if (readOnly) return;
    // Check all dishes for the component line, not just selected dish
    let line: QuoteLine | null = null;
    if (selectedDish?.componentLines[componentName]) {
      line = selectedDish.componentLines[componentName];
    } else {
      for (const dish of dishes) {
        if (dish.componentLines[componentName]) {
          line = dish.componentLines[componentName];
          break;
        }
      }
    }
    setSelectedComponent(componentName);
    setSelectedLine(line);
    setMapDrawerOpen(true);
  };

  // NOTE: the legacy handleApplyMapping/updateDishProduct/handleReplaceMatch/
  // handleAddToQuote client-side-mutation handlers were removed when
  // MapComponentDrawer (single-select) was swapped for MatchDrawer
  // (multi-select "Your Call"). MatchDrawer submits directly to the BE
  // (submitYourCallSelection) and the result is reflected via a full
  // handleMatchesUpdated() refetch instead of local optimistic patching.

  const toggleCategory = (id: string) => {
    setExpandedCategories(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  // ─── Loading screen ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#FFF9F3]">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin text-[#F2993D] mx-auto" />
          <p className="text-sm text-gray-500">{loadingStatus}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#FFF9F3]">
        <div className="text-center space-y-4 max-w-sm px-4">
          <p className="text-red-500 text-sm">{error}</p>
          <Button
            onClick={() => navigate('/start-new-quote')}
            className="bg-[#7FAEC2] hover:bg-[#6A9AB0] text-white"
          >
            Start Over
          </Button>
        </div>
      </div>
    );
  }

  // ─── Render component row ─────────────────────────────────────────────────

  const renderComponentRow = (component: string, line?: QuoteLine) => {
    const bestMatch = line?.product;
    const bestCandidate = line?.alignment_candidates?.find(c => c.position === 1);
    const confidence = bestCandidate?.score != null ? Math.round(bestCandidate.score * 100) : null;
    const isMapped = mappedComponents[component]?.length > 0;

    const badge = isMapped
      ? { text: 'Your Pick', cls: 'bg-green-100 text-green-700' }
      : confidence != null
        ? confidence >= 90 ? { text: 'Strong Match', cls: 'bg-green-100 text-green-700' }
          : confidence >= 70 ? { text: 'Good Match', cls: 'bg-[#A5CFDD]/20 text-[#2A5F6F]' }
          : confidence >= 50 ? { text: 'Review Suggested', cls: 'bg-amber-100 text-amber-700' }
          : { text: 'Needs Your Call', cls: 'bg-red-100 text-red-700' }
        : !bestMatch ? { text: 'Needs Your Call', cls: 'bg-red-100 text-red-700' }
        : null;

    const bestMatchIsRepMemory = bestCandidate?.rep_memory === true;
    const bestMatchLocked = bestMatch ? (lockOverrides[bestMatch.id] ?? bestMatchIsRepMemory) : false;
    const bestMatchIsDistributorMemory = !bestMatchIsRepMemory && bestCandidate?.distributor_memory === true;
    const canonicalKey = line?.component?.canonical_key ?? null;

    const handleToggleLock = async () => {
      // P0 route/shell guard fix round 2: call-site re-check. The
      // ChainToggle control is already hidden on a read-only render, but a
      // stale tab or a direct call must not be able to write anyway.
      if (readOnly) return;
      if (!quoteId || !line?.id || !bestMatch || lockPending) return;
      setLockError(null);
      setLockPending(bestMatch.id);
      const nextLocked = !bestMatchLocked;
      const res = await toggleRepMemoryLock(quoteId, {
        quote_line_id: line.id,
        product_id: bestMatch.id,
        canonical_key: canonicalKey,
        locked: nextLocked,
      });
      setLockPending(null);
      if (res.error) {
        // Show the server's own copy. rep_memory_lock rejects with text written
        // for a person; discarding it made the chain look like a dead control.
        setLockError({ productId: bestMatch.id, message: res.error });
        return;
      }
      setLockOverrides(prev => ({ ...prev, [bestMatch.id]: nextLocked }));
    };

    return (
      <div key={component} className="grid grid-cols-[1fr_1fr_auto] items-center gap-4 py-3 border-b border-gray-100 last:border-b-0">
        {/* Left: ingredient name */}
        <span className="text-sm font-medium text-[#2A2A2A] truncate">{toTitleCase(component)}</span>

        {/* Center: matched product info + badge */}
        <div className="min-w-0">
          {bestMatch ? (
            <div className="text-xs text-gray-500">
              <p className="font-medium truncate flex items-center gap-1">
                <span className="truncate">{formatProductName(bestMatch.product, bestMatch.brand)}</span>
                {quoteId && line?.id && !readOnly && (
                  <ChainToggle
                    locked={bestMatchLocked}
                    onToggle={handleToggleLock}
                    disabled={lockPending === bestMatch.id}
                  />
                )}
                {lockError?.productId === bestMatch.id && (
                  <span className="text-[11px] font-normal" style={{ color: '#B23A34' }}>{lockError.message}</span>
                )}
                {bestMatchIsDistributorMemory && (
                  <DistributorMemoryBadge
                    distributorName={bestCandidate?.distributor_name}
                    signalType={bestCandidate?.distributor_signal_type}
                    mandateReason={bestCandidate?.distributor_mandate_reason}
                    mandateSetBy={bestCandidate?.distributor_mandate_set_by}
                  />
                )}
              </p>
              <p className="text-gray-400 truncate">{bestMatch.item_number} &middot; {bestMatch.pack_size}</p>
              {badge && (
                <span className={`mt-1 inline-block rounded px-2 py-0.5 text-xs font-medium ${badge.cls}`}>
                  {badge.text}
                </span>
              )}
            </div>
          ) : (
            <div className="text-xs">
              {line?.chef_note?.includes('Category not carried') && (
                <span className="italic text-amber-600">Category not carried</span>
              )}
              {badge && (
                <span className={`mt-1 inline-block rounded px-2 py-0.5 text-xs font-medium ${badge.cls}`}>
                  {badge.text}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right: action button — hidden on a read-only render (admin viewer
            / sent quote); the server would refuse the resulting write. */}
        {!readOnly && (
          <Button
            size="sm"
            className={`text-xs px-3 py-1 whitespace-nowrap ${isMapped ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-[#A5CFDD] hover:bg-[#8db9c9] text-[#2A2A2A]'}`}
            onClick={() => handleMapComponent(component)}
          >
            {isMapped ? 'Change Match' : 'Add Match'}
          </Button>
        )}
      </div>
    );
  };

  // ─── Main render ──────────────────────────────────────────────────────────

  return (
    <div className="flex h-screen bg-[#FFF9F3]">
      {/* Left Sidebar */}
      <div className="hidden md:flex w-64 bg-white border-r border-gray-200 flex-col h-full min-h-0">
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-sm font-medium text-gray-500 mb-2">DISHES</h3>
        </div>
        <div className="flex-1 overflow-y-auto min-h-0">
          {dishes.map((dish) => (
            <button
              key={dish.id}
              onClick={() => { setSelectedDish(dish); setSelectedTab('dishes'); }}
              className={`w-full p-4 text-left border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                selectedDish?.id === dish.id ? 'bg-[#A5CFDD]/10 border-l-4 border-l-[#A5CFDD]' : ''
              }`}
            >
              <div className="flex items-start gap-2">
                {/* P0 route/shell guard: no active-looking dish checkbox on a
                    read-only render (admin viewer / sent quote). */}
                {!readOnly && <input type="checkbox" className="mt-1" checked readOnly />}
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#2A2A2A]">{dish.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{dish.components.length} ingredients</p>
                </div>
              </div>
            </button>
          ))}
          {/* Additions Section */}
          {additions.length > 0 && (
            <div className="border-t border-gray-200">
              <div className="p-4 pb-2">
                <h3 className="text-sm font-medium text-gray-500">ADDITIONS ({additions.length})</h3>
              </div>
              <div className="px-4 pb-2 space-y-1">
                {additions.map((addition) => (
                  <div
                    key={addition.id}
                    className="flex items-center justify-between py-2 px-2 rounded-md bg-green-50 border border-green-100"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#2A2A2A] truncate">
                        {toTitleCase(addition.product.brand)} {toTitleCase(addition.product.product)}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {addition.type === 'add_to_quote' ? `+ ${toTitleCase(addition.componentName)}` : toTitleCase(addition.sourceDish)}
                      </p>
                    </div>
                    <button
                      onClick={() => setAdditions(prev => prev.filter(a => a.id !== addition.id))}
                      className="ml-2 text-gray-400 hover:text-red-500 shrink-0"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {!readOnly && (
          <div className="p-4 flex-shrink-0 border-t border-gray-100 pb-[112px]">
            <Button
              onClick={() => setIsAddDishDrawerOpen(true)}
              className="w-full h-auto py-3 bg-[#7FAEC2] hover:bg-[#6A9AB0] text-white whitespace-normal text-center"
            >
              <Plus className="w-4 h-4 mr-2 shrink-0" />
              Manually Add A Dish or Ingredient
            </Button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <div className="bg-white border-b border-gray-200 px-4 md:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 max-w-6xl mx-auto">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button
                onClick={() => navigate('/start-new-quote')}
                className="text-gray-400 hover:text-gray-600"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl text-[#4F4F4F]">Match Ingredients</h1>
                  {readOnlyMarker && (
                    <span
                      className="inline-flex items-center gap-1 text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200 rounded-full px-2.5 py-1"
                      data-testid="quote-read-only-marker"
                    >
                      <Lock className="w-3 h-3" />
                      {readOnlyMarker}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  {readOnly
                    ? 'Viewing matches for this quote. Changes are not available.'
                    : 'Select product matches for each ingredient'}
                </p>
              </div>
            </div>
            {/* Single Adjust Pricing button is in the sticky footer */}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto p-4 md:p-6">
            {/* Mobile Dish Selector */}
            <div className="md:hidden mb-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <label className="text-sm font-medium text-gray-500 mb-2 block">Current Dish</label>
              <button
                className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-sm bg-white text-[#2A2A2A]"
                onClick={() => setIsDishListDrawerOpen(true)}
              >
                <span className="truncate font-medium">{selectedDish?.name || 'Select a dish'}</span>
                <div className="flex items-center gap-2 text-gray-500">
                  <span className="text-xs">({selectedDish?.components.length ?? 0})</span>
                  <ChevronDown className="w-4 h-4" />
                </div>
              </button>
            </div>

            {/* Main Card with Tabs */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="flex border-b border-gray-200">
                {([
                  { key: 'categories', label: 'Categories' },
                  { key: 'dishes', label: 'Dishes' },
                  { key: 'match-strength', label: 'Match Strength' },
                ] as const).map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setSelectedTab(tab.key)}
                    className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
                      selectedTab === tab.key
                        ? 'border-[#A5CFDD] text-[#A5CFDD]'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Filter Bar */}
              <div className="flex flex-wrap items-center gap-2 px-6 py-3 border-b border-gray-100 bg-gray-50/50">
                <FilterDropdown
                  label="Match Status"
                  options={[...MATCH_STATUS_OPTIONS]}
                  selected={statusFilter}
                  onToggle={(v) => toggleFilter(setStatusFilter, v)}
                  onClear={() => setStatusFilter(new Set())}
                />
                <FilterDropdown
                  label="Category"
                  options={availableCategories}
                  selected={categoryFilter}
                  onToggle={(v) => toggleFilter(setCategoryFilter, v)}
                  onClear={() => setCategoryFilter(new Set())}
                  formatOption={categoryLabel}
                />
                <FilterDropdown
                  label="Brand"
                  options={availableBrands}
                  selected={brandFilter}
                  onToggle={(v) => toggleFilter(setBrandFilter, v)}
                  onClear={() => setBrandFilter(new Set())}
                />
                {hasActiveFilters && (
                  <>
                    <span className="text-xs text-gray-500 ml-2">
                      Showing {filteredCount} of {allLines.length} item{allLines.length === 1 ? '' : 's'}
                    </span>
                    <button
                      onClick={clearAllFilters}
                      className="text-xs text-[#A5CFDD] hover:text-[#7FAEC2] ml-1 underline"
                    >
                      Clear Filters
                    </button>
                  </>
                )}
              </div>

              <div className="p-6">
                {selectedTab === 'dishes' ? (
                  <div>
                    <h2 className="text-lg font-medium text-[#4F4F4F] mb-2">
                      {selectedDish?.name || 'No dish selected'}
                    </h2>
                    <p className="text-sm text-gray-500 mb-6">
                      Select a match for each ingredient from your distributor's catalog
                    </p>
                    <div className="space-y-3">
                      {(selectedDish?.components || []).filter(component =>
                        passesFilters(component, selectedDish?.componentLines[component])
                      ).map(component =>
                        renderComponentRow(component, selectedDish?.componentLines[component])
                      )}
                      {hasActiveFilters && (selectedDish?.components || []).every(component =>
                        !passesFilters(component, selectedDish?.componentLines[component])
                      ) && (
                        <p className="text-sm text-gray-400 text-center py-8">No ingredients match the active filters</p>
                      )}
                    </div>
                  </div>
                ) : selectedTab === 'match-strength' ? (
                  /* Match Strength — group by match quality */
                  <div>
                    <h2 className="text-lg font-medium text-[#4F4F4F] mb-2">By Match Strength</h2>
                    <p className="text-sm text-gray-500 mb-4">Focus on ingredients that need the most attention</p>

                    <div className="flex flex-wrap gap-2 mb-6">
                      {([
                        { key: 'no-match', label: 'No Match', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
                        { key: 'needs-review', label: 'Needs Review', cls: 'bg-amber-50 text-amber-700 border-amber-300' },
                        { key: 'good-match', label: 'Good Match', cls: 'bg-[#A5CFDD]/10 text-[#2A5F6F] border-[#A5CFDD]' },
                        { key: 'strong-match', label: 'Strong Match', cls: 'bg-green-50 text-green-700 border-green-300' },
                        { key: 'your-pick', label: 'Your Pick', cls: 'bg-green-50 text-green-700 border-green-300' },
                      ] as const).map(filter => {
                        const active = strengthFilters.has(filter.key);
                        return (
                          <button
                            key={filter.key}
                            onClick={() => setStrengthFilters(prev => {
                              const next = new Set(prev);
                              next.has(filter.key) ? next.delete(filter.key) : next.add(filter.key);
                              return next;
                            })}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                              active ? filter.cls : 'bg-white text-gray-400 border-gray-200'
                            }`}
                          >
                            {filter.label}
                          </button>
                        );
                      })}
                    </div>

                    <div className="space-y-2">
                      {(() => {
                        type StrengthGroup = { key: string; label: string; badgeCls: string; items: { component: string; line: QuoteLine }[] };
                        const groups: StrengthGroup[] = [
                          { key: 'no-match', label: 'No Match', badgeCls: 'bg-gray-100 text-gray-600', items: [] },
                          { key: 'needs-review', label: 'Needs Review', badgeCls: 'bg-amber-100 text-amber-700', items: [] },
                          { key: 'good-match', label: 'Good Match', badgeCls: 'bg-[#A5CFDD]/20 text-[#2A5F6F]', items: [] },
                          { key: 'strong-match', label: 'Strong Match', badgeCls: 'bg-green-100 text-green-700', items: [] },
                          { key: 'your-pick', label: 'Your Pick', badgeCls: 'bg-green-100 text-green-700', items: [] },
                        ];

                        const seenProducts = new Set<string>();
                        for (const dish of dishes) {
                          for (const comp of dish.components) {
                            const line = dish.componentLines[comp];
                            const productKey = line?.product?.id || comp;
                            if (seenProducts.has(productKey)) continue;
                            seenProducts.add(productKey);

                            // Apply column filters
                            if (!passesFilters(comp, line)) continue;

                            // User manually picked → goes to "Your Pick" group
                            if (mappedComponents[comp]?.length > 0) {
                              groups[4].items.push({ component: comp, line });
                              continue;
                            }

                            const bestCandidate = line?.alignment_candidates?.find(c => c.position === 1);
                            const score = bestCandidate?.score != null ? Math.round(bestCandidate.score * 100) : null;

                            if (!line?.product || score === null || score < 50) {
                              groups[0].items.push({ component: comp, line });
                            } else if (score < 70) {
                              groups[1].items.push({ component: comp, line });
                            } else if (score < 90) {
                              groups[2].items.push({ component: comp, line });
                            } else {
                              groups[3].items.push({ component: comp, line });
                            }
                          }
                        }

                        const filtered = groups.filter(g => strengthFilters.has(g.key) && g.items.length > 0);

                        if (filtered.length === 0) {
                          return (
                            <p className="text-sm text-gray-400 text-center py-8">
                              No ingredients match the selected filters
                            </p>
                          );
                        }

                        return filtered.map(group => (
                          <div key={group.key} className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="flex items-center justify-between p-4 bg-gray-50">
                              <div className="flex items-center gap-3">
                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${group.badgeCls}`}>
                                  {group.label}
                                </span>
                                <span className="text-sm text-gray-500">{group.items.length} ingredient{group.items.length !== 1 ? 's' : ''}</span>
                              </div>
                            </div>
                            <div className="border-t border-gray-200">
                              {group.items.map(({ component, line }) => (
                                <div key={component} className="pl-4">
                                  {renderComponentRow(component, line)}
                                </div>
                              ))}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                ) : (
                  /* Categories — group all components by product category */
                  <div>
                    <h2 className="text-lg font-medium text-[#4F4F4F] mb-2">By Category</h2>
                    <p className="text-sm text-gray-500 mb-6">Ingredients organized by product category</p>
                    <div className="space-y-2">
                      {(() => {
                        const categoryMap: Record<string, { component: string; line: QuoteLine }[]> = {};
                        const seenProducts = new Set<string>();
                        for (const dish of dishes) {
                          for (const comp of dish.components) {
                            const line = dish.componentLines[comp];
                            const productKey = line?.product?.id || comp;
                            if (seenProducts.has(productKey)) continue;
                            seenProducts.add(productKey);
                            // Apply column filters
                            if (!passesFilters(comp, line)) continue;
                            const cat = line?.category || 'Uncategorized';
                            if (!categoryMap[cat]) categoryMap[cat] = [];
                            categoryMap[cat].push({ component: comp, line });
                          }
                        }
                        const sortedCategories = Object.keys(categoryMap).sort();
                        if (sortedCategories.length === 0 && hasActiveFilters) {
                          return (
                            <p className="text-sm text-gray-400 text-center py-8">No ingredients match the active filters</p>
                          );
                        }
                        return sortedCategories.map(cat => {
                          const items = categoryMap[cat];
                          const isExpanded = expandedCategories.includes(cat);
                          const mappedCount = items.filter(i => mappedComponents[i.component]?.length > 0).length;
                          return (
                            <div key={cat} className="border border-gray-200 rounded-lg overflow-hidden">
                              <button
                                onClick={() => toggleCategory(cat)}
                                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center text-gray-400">
                                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                  </div>
                                  <span className="text-sm font-medium text-[#2A2A2A]">
                                    {categoryLabel(cat)} ({mappedCount}/{items.length})
                                  </span>
                                </div>
                                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </button>
                              {isExpanded && (
                                <div className="border-t border-gray-200 bg-gray-50">
                                  {items.map(({ component, line }) =>
                                    <div key={component} className="pl-8">
                                      {renderComponentRow(component, line)}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Match Ingredient Drawer — multi-select "Your Call" redesign (checkbox-only:
          first pick replaces the current match, the rest add alongside it). */}
      <MatchDrawer
        open={mapDrawerOpen}
        onOpenChange={setMapDrawerOpen}
        ingredientName={selectedComponent}
        currentProduct={selectedLine?.product || null}
        candidates={selectedLine?.alignment_candidates || []}
        onFindMoreMatches={quoteId && selectedLine && (selectedLine?.alignment_candidates?.length ?? 0) > 0 ? async () => {
          const res = await getMoreMatches(quoteId, selectedLine.id);
          return res.data?.candidates || [];
        } : undefined}
        readOnly={readOnly}
        readOnlyMarker={readOnlyMarker}
        quoteId={quoteId || undefined}
        quoteLineId={selectedLine?.id || null}
        dishComponentId={selectedLine?.component?.id || null}
        canonicalKey={selectedLine?.component?.canonical_key ?? null}
        onSubmitted={(pickedProductIds) => {
          setMappedComponents(prev => ({ ...prev, [selectedComponent]: pickedProductIds }));
          handleMatchesUpdated();
        }}
      />

      {/* Mobile Dish List Drawer — canonical vaul-based primitive (was a
          hand-rolled fixed-inset overlay; structural alignment only, same
          inner markup/behavior). */}
      <Drawer open={isDishListDrawerOpen} onOpenChange={setIsDishListDrawerOpen} direction="right">
        <DrawerContent className="w-full max-w-xs h-full flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-[#2A2A2A]">Select Dish</h2>
            <button onClick={() => setIsDishListDrawerOpen(false)} className="p-2 hover:bg-gray-100 rounded-full">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {dishes.map(dish => (
              <button
                key={dish.id}
                onClick={() => { setSelectedDish(dish); setIsDishListDrawerOpen(false); }}
                className={`w-full p-4 text-left border-b border-gray-100 hover:bg-gray-50 ${selectedDish?.id === dish.id ? 'bg-[#A5CFDD]/10 border-l-4 border-l-[#A5CFDD]' : ''}`}
              >
                <p className="text-sm font-medium text-[#2A2A2A]">{dish.name}</p>
                <p className="text-xs text-gray-500 mt-1">{dish.components.length} ingredients</p>
              </button>
            ))}
          </div>
          {!readOnly && (
            <div className="p-4 border-t border-gray-100 bg-gray-50">
              <Button onClick={() => setIsAddDishDrawerOpen(true)} className="w-full bg-[#7FAEC2] hover:bg-[#6A9AB0] text-white">
                <Plus className="w-4 h-4 mr-2" /> Manually Add A Dish
              </Button>
            </div>
          )}
        </DrawerContent>
      </Drawer>

      {/* Add Dish Drawer — canonical vaul-based primitive (was a hand-rolled
          fixed-inset overlay; structural alignment only, same inner
          markup/behavior). */}
      <Drawer open={isAddDishDrawerOpen} onOpenChange={setIsAddDishDrawerOpen} direction="right">
        <DrawerContent className="w-full max-w-md h-full flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-[#2A2A2A]">Manually Add Dish</h2>
            <button onClick={() => setIsAddDishDrawerOpen(false)} className="p-2 hover:bg-gray-100 rounded-full">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="dish-name">Dish Name (Optional)</Label>
              <Input
                id="dish-name"
                placeholder="e.g. Spaghetti Carbonara"
                value={newDishName}
                onChange={(e) => setNewDishName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dish-components">List Ingredients</Label>
              <p className="text-xs text-gray-500">Enter each ingredient on a new line.</p>
              <Textarea
                id="dish-components"
                placeholder={"Spaghetti\nPancetta\nEggs\nParmesan cheese\nBlack pepper"}
                className="min-h-[200px]"
                value={newDishComponents}
                onChange={(e) => setNewDishComponents(e.target.value)}
              />
            </div>
          </div>
          <div className="p-4 border-t border-gray-100 bg-gray-50">
            <Button
              onClick={handleAlignWithCatalog}
              className="w-full bg-[#7FAEC2] hover:bg-[#6A9AB0] text-white"
              disabled={!newDishComponents.trim() || addDishLoading}
            >
              {addDishLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Matching…</>
              ) : 'Match With Catalog'}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Floating Adjust Pricing button — bottom-right pill (right-justified
          per Moose's 100km Foods demo feedback: the old bottom-center
          placement sat on top of the match rows), matches the Finish Quote
          button pattern on QuoteBuilderPage (same right-anchored position,
          rounded-full, shadow-lg, color/sizing). Fixed independently of
          QuoteReviewBar so it never overlaps the DISHES sidebar and never
          jumps when the review card above it dismisses (BUG #30/#31). */}
      <button
        onClick={() => navigate('/quote-builder', { state: { quoteId, isOpenQuote, locationId } })}
        className="fixed bottom-6 right-4 md:right-6 z-50 md:min-w-[200px] bg-[#F2993D] hover:bg-[#E8953A] text-white font-medium py-3 px-6 rounded-full shadow-lg text-base min-h-[48px]"
      >
        {/* Read-only render: navigation stays (the builder is itself locked)
            but the label must not promise a write. */}
        {readOnly ? 'View Pricing' : 'Adjust Pricing'}
      </button>

      {/* Quote review bar — the Looks good / Needs fixes feedback loop is a
          write path (reviewQuote + match redo), so it never renders on a
          read-only view (admin viewer / sent quote).

          It also never renders before there is something to review (Justin's
          third criterion). `quoteId` alone was the old gate, and a quoteId
          exists from the moment the quote is created, so the card could ask
          "How do these matches look?" over a still-processing or empty list.
          Now it waits for matching to have finished AND produced at least one
          match. */}
      {quoteId && !readOnly && matchingComplete && matchCount > 0 && (
        <QuoteReviewBar quoteId={quoteId} onMatchesUpdated={handleMatchesUpdated} />
      )}

    </div>
  );
}
